import { Step, StepLabel, Stepper } from "@mui/material";
import {
  ComgateOnboardingStatus,
  OrbitLPhase,
  ProductCode,
} from "Infrastructure/Api/Api";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { useGetOrbitLStatusQuery } from "Api/Queries/Merchants/useGetOrbitLStatusQuery";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.Progress;

type Props = {
  merchantPublicID: string;
};

// Map 7 phases to 5 visual steps
const PHASE_TO_STEP: Record<OrbitLPhase, number> = {
  [OrbitLPhase.RequiresCompletion]: 0,
  [OrbitLPhase.ReadyToDownloadContract]: 1,
  [OrbitLPhase.ContractDownloaded]: 1,
  [OrbitLPhase.SubmittedToComgate]: 2,
  [OrbitLPhase.ProcessingByComgate]: 2,
  [OrbitLPhase.TerminalDispatched]: 3,
  [OrbitLPhase.Completed]: 4,
};

const ERROR_COMGATE_STATUSES: Array<ComgateOnboardingStatus> = [
  ComgateOnboardingStatus.ERRORS_IN_DOCUMENTS,
  ComgateOnboardingStatus.REJECTED,
];

export const OrbitLProgress = ({ merchantPublicID }: Props) => {
  const { t } = useResource();
  const { data: status, isLoading } = useGetOrbitLStatusQuery(merchantPublicID);
  const { data: merchantProducts } =
    useGetMerchantAppProductsQuery(merchantPublicID);

  if (isLoading || !status) {
    return null;
  }

  // Nothing ships for a SoftPOS-only merchant; the fourth step is handing over the activation code.
  const hasOrbitLProduct = !!merchantProducts?.some(
    (p) => p.productDto.code === ProductCode.OrbitL,
  );
  const steps = [
    t(RESOURCES.Steps.Completion),
    t(RESOURCES.Steps.Signing),
    t(RESOURCES.Steps.Processing),
    t(
      hasOrbitLProduct
        ? RESOURCES.Steps.Dispatch
        : RESOURCES.Steps.DispatchSoftPos,
    ),
    t(RESOURCES.Steps.Done),
  ];

  const activeStep = PHASE_TO_STEP[status.phase] ?? 0;
  const isError =
    status.comgateStatus != null &&
    ERROR_COMGATE_STATUSES.includes(status.comgateStatus);

  return (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      sx={{
        "& .MuiStepLabel-label": { fontSize: "0.7rem" },
      }}
    >
      {steps.map((label, index) => (
        <Step key={label} completed={index < activeStep}>
          <StepLabel error={isError && index === activeStep}>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};
